import React, { HTMLAttributes, ReactNode, useState } from 'react';
import classes from './PlanContainer.module.scss';
import { Typography } from 'antd';
import { RxCaretDown } from 'react-icons/rx';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { colorToHex, splitBalance } from 'shared/libs/utils/helpers';
import clsx from 'clsx';
import ActivityIndicator from '../ActivityIndicator/ActivityIndicator';
import { AddAccount } from 'features/account/add-account';
import { useIntl } from 'react-intl';

interface PlanContainerProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  active: boolean;
  availBal?: number;
  loading?: boolean;
  disabled?: boolean;
  available?: boolean;
  items?: Array<object>;
  renderItems?: (item: any, secured?: boolean) => React.ReactNode;
  footer?: ReactNode | (() => ReactNode);
  id?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => any;
  secured?: boolean;
  setSecured: (s: boolean) => void;
}
const PlanContainer: React.FC<PlanContainerProps> = ({
  title,
  footer,
  renderItems,
  items,
  active,
  available,
  disabled,
  loading,
  availBal,
  onClick,
  id,
  secured,
  setSecured,
  ...props
}) => {
  const [expanded, setExpanded] = useState(false);
  const [wholePart, fractionalPart] = splitBalance(availBal);
  const { formatMessage: t } = useIntl();
  const hasBalance = availBal !== undefined && availBal !== null;

  const onExpand = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <div {...props} className={clsx(classes.wrapper, props.className)}>
      <div
        id={id + '-plan-container'}
        onClick={active ? onExpand : onClick}
        className={classes.container}
        style={{
          background: `linear-gradient(135.00deg, rgb(193, 236, 241) 0%,rgb(195, 230, 246) 100%)`
        }}
      >
        <Typography.Text ellipsis className={classes.title}>
          {title.toUpperCase()}
        </Typography.Text>

        {active ? (
          <div className={classes.secondRow}>
            <div className={classes.head}>
              {hasBalance ? (
                <Typography.Text
                  className={classes.balance}
                  id={secured ? 'plan-balance-hidden' : 'plan-balance-shown'}
                >
                  {secured ? '*' : wholePart},
                  <Typography.Text className={classes.fractionalPart}>
                    {secured ? '**' : fractionalPart} €
                  </Typography.Text>
                </Typography.Text>
              ) : (
                <Typography.Text
                  className={classes.balance}
                  id={secured ? 'plan-balance-hidden' : 'plan-balance-shown'}
                >
                  {secured ? '*' : '--'}
                </Typography.Text>
              )}
              <div
                style={{ zIndex: 9 }}
                id={id ? `btn__${id}-show-balance` : ''}
                className={classes.caretIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  setSecured(!secured);
                }}
              >
                {secured ? (
                  <EyeOutlined className={classes.icon} />
                ) : (
                  <EyeInvisibleOutlined className={classes.icon} />
                )}
              </div>
            </div>
            {items && items.length > 0 && (
              <div
                onClick={onExpand}
                className={clsx(classes.caretIcon, expanded && classes.expanded)}
              >
                <RxCaretDown />
              </div>
            )}
          </div>
        ) : (
          <div className={classes.actionsContainer}>
            <AddAccount type='button' />
          </div>
        )}

        {footer ? (typeof footer === 'function' ? footer() : footer) : null}

        {(disabled || loading || available === false) && (
          <div className={classes.disabledOverlay}>
            {available === false ? (
              <div className={classes.notAvailableContainer}>
                <Typography.Text className={classes.notAvailableText}>
                  {t({ id: 'plan.notAvailable' })}
                </Typography.Text>
              </div>
            ) : (
              <>
                {loading && (
                  <div className={classes.loadingContainer}>
                    <div className={classes.indicatorContainer}>
                      <ActivityIndicator />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {items && items.length > 0 && (
        <div
          className={clsx(classes.itemsContainer, expanded && classes.expanded)}
          style={{ background: `${colorToHex('rgb(193, 236, 241)')}55` }}
        >
          {renderItems && renderItems(items, secured)}
        </div>
      )}
    </div>
  );
};

export default React.memo(PlanContainer);
